import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Shield, Eye, EyeOff, CheckCircle2, XCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { masterPasswordAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const MasterPasswordPage = () => {
  const { businessId } = useParams();
  const [isSet, setIsSet] = useState(null); // null = loading
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await masterPasswordAPI.getStatus();
      setIsSet(res.data.is_set);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to check master password status'));
    }
  };

  const passwordChecks = [
    { label: 'At least 8 characters', met: form.newPassword.length >= 8 },
    { label: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(form.newPassword) },
    { label: 'One lowercase letter (a-z)', met: /[a-z]/.test(form.newPassword) },
    { label: 'One digit (0-9)', met: /\d/.test(form.newPassword) },
    { label: 'One special character (!@#$...)', met: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(form.newPassword) },
    { label: 'Passwords match', met: form.newPassword && form.newPassword === form.confirmPassword },
  ];

  const allChecksMet = passwordChecks.every((c) => c.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allChecksMet) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      if (isSet) {
        await masterPasswordAPI.change({
          current_password: form.currentPassword,
          new_password: form.newPassword,
          confirm_password: form.confirmPassword,
        });
        toast.success('Master password changed successfully');
      } else {
        await masterPasswordAPI.set({
          new_password: form.newPassword,
          confirm_password: form.confirmPassword,
        });
        toast.success('Master password set successfully');
      }
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      fetchStatus();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save master password'));
    } finally {
      setLoading(false);
    }
  };

  const toggleShow = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  if (isSet === null) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[80vh] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 mb-3">
            <Shield className="h-6 w-6 text-indigo-600" />
          </div>
          <CardTitle className="text-xl">Master Password</CardTitle>
          <CardDescription>
            {isSet
              ? 'Your master password is active. You can use it to log into any staff account.'
              : 'Set a master password to log into any staff account using their email.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Status Badge */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-5 text-sm font-medium ${
            isSet
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            {isSet ? (
              <><CheckCircle2 className="h-4 w-4" /> Master password is configured</>
            ) : (
              <><Info className="h-4 w-4" /> No master password set</>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current password (only when changing) */}
            {isSet && (
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Master Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={form.currentPassword}
                    onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                    placeholder="Enter current master password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => toggleShow('current')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* New password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {isSet ? 'New Master Password' : 'Master Password'}
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={form.newPassword}
                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                  placeholder="Enter a strong password"
                  required
                />
                <button
                  type="button"
                  onClick={() => toggleShow('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  placeholder="Re-enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => toggleShow('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Requirements checklist */}
            {form.newPassword && (
              <div className="rounded-lg border p-3 space-y-1.5">
                {passwordChecks.map((check, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    {check.met ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                    )}
                    <span className={check.met ? 'text-green-700' : 'text-gray-500'}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              disabled={loading || !allChecksMet || (isSet && !form.currentPassword)}
            >
              {loading
                ? (isSet ? 'Changing...' : 'Setting...')
                : (isSet ? 'Change Master Password' : 'Set Master Password')}
            </Button>
          </form>

          {/* How it works */}
          <div className="mt-6 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs font-semibold text-slate-600 mb-1.5">How it works</p>
            <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside">
              <li>Use any staff member's email + this master password to log in as them</li>
              <li>Works on the regular login page -- no separate entry point</li>
              <li>Only works for staff accounts, not owner accounts</li>
              <li>Each master login is recorded in the audit log</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MasterPasswordPage;
